import { useRef, useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  InsightsFilter,
  InsightsFilterV2,
  uuidv4,
  insightsFilterExpressionSchema,
  InsightsFilterExpression,
  InsightsFilterExpressionNode,
  InsightsMetricOperationV2,
  convertSimpleInsightsFiltersToExpression,
} from '@magicbrief/common';
import { Heading } from 'react-aria-components';
import { Button } from '@magicbrief/ui/src/components/button';
import Input from 'src/components/Input';
import Plus from 'src/assets/svgicons/line/plus.svg';
import Trash01 from 'src/assets/svgicons/duocolor/trash-01.svg';
import RefreshCw05 from 'src/assets/svgicons/line/refresh-cw-05.svg';
import { useI18nContext } from 'src/i18n/i18n-react';
import { InsightsFilterSet } from '../routes/InsightsCompare/types';
import {
  transformInsightsFilterMetric,
  transformInsightsFilterOperation,
} from '../util/useFilterFormV2';
import { InsightsFormFilterMetricFieldSelect } from './InsightsFilter/components/InsightsFilterMetricFieldSelect';
import { InsightsFormFilterMetricOperationSelect } from './InsightsFilter/components/InsightsFilterMetricOperationSelect';
import { InsightsFilterFormFieldValueInputs } from './InsightsFilter/components/InsightsFilterMetricItemEdit';

const SCHEMA = z.object({
  expression: insightsFilterExpressionSchema,
});

type InsightsExpressionForm = { expression: InsightsFilterExpression };

export const InsightsAdvancedFilterForm: React.FC<
  React.PropsWithChildren<{
    filterSet: InsightsFilterSet | null;
  }>
> = ({ children, filterSet }) => {
  const { handleSubmit, ...formMethods } = useForm<InsightsExpressionForm>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(SCHEMA),
    defaultValues: {
      expression: filterSet
        ? convertSimpleInsightsFiltersToExpression(filterSet.filter)
        : [
            {
              operand: {
                expressions: [
                  {
                    operand: {
                      field: 'adName',
                      operation: 'contains',
                      value: '',
                    },
                    operator: 'or',
                  },
                ],
              },
              operator: 'or',
            },
          ],
    },
  });

  return (
    <FormProvider handleSubmit={handleSubmit} {...formMethods}>
      {children}
    </FormProvider>
  );
};

export const InsightsAdvancedFilters: React.FC<{
  filterSet: InsightsFilterSet | null;
  onComplete: (filterSet: InsightsFilterSet) => void;
  onClose: () => void;
  platform: 'facebook' | 'tiktok';
  customEvents: string[];
  customConversions: Array<{ facebookId: string; name: string }>;
  isAdvancedFilter: boolean;
}> = ({
  filterSet,
  onComplete,
  onClose,
  customEvents,
  customConversions,
  platform,
  isAdvancedFilter,
}) => {
  const { LL } = useI18nContext();
  const nameRef = useRef<HTMLInputElement | null>(null);
  const { handleSubmit, control } = useFormContext<InsightsExpressionForm>();
  const { fields, append, remove, update } =
    useFieldArray<InsightsExpressionForm>({ name: 'expression', control });

  const [nameError, setNameError] = useState<string | null>(null);

  const onSubmit = handleSubmit((values) => {
    if (
      !isAdvancedFilter &&
      (!nameRef.current || !(nameRef.current.value.trim().length > 0))
    ) {
      setNameError(LL.errors.fieldRequired());
      return;
    }
    onComplete({
      name: isAdvancedFilter ? 'Advanced Filter' : nameRef.current?.value || '',
      filter: values['expression'].filter(
        (x) =>
          x.operand &&
          ('expressions' in x.operand ? !!x.operand.expressions.length : true)
      ),
      id: filterSet?.id ?? uuidv4(),
    });
  });

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-10 p-4">
      <div className="flex flex-col gap-4">
        {isAdvancedFilter ? (
          <Heading className="mb-2 text-xl font-semibold text-primary">
            Advanced Filter
          </Heading>
        ) : (
          <Input
            ref={nameRef}
            defaultValue={
              isAdvancedFilter ? 'Advanced Filter' : (filterSet?.name ?? '')
            }
            label=""
            name="groupName"
            placeholder="Group name"
            error={nameError}
          />
        )}
        {fields.map((field, i) => (
          <div className="flex flex-row gap-2" key={field.id}>
            {i === 0 ? (
              <div className="h-min w-20 rounded-md border border-dashed border-primary/40 p-2.5 px-3 py-2 text-center align-middle text-sm text-primary/40">
                Where
              </div>
            ) : (
              <Button
                variant="secondary"
                size="small"
                className="w-20"
                onPress={() => {
                  update(i - 1, {
                    ...fields[i - 1],
                    operator: fields[i - 1].operator === 'and' ? 'or' : 'and',
                  });
                }}
              >
                <RefreshCw05 className="size-4" />
                {fields[i - 1].operator}
              </Button>
            )}
            <div className="w-full">
              <InsightsExpressionFormExpressionNode
                platform={platform}
                field={field}
                deleteGroup={() => remove(i)}
                name={`expression.${i}`}
                customEvents={customEvents}
                customConversions={customConversions}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-row justify-between">
        <Button
          variant="secondary"
          size="small"
          onPress={() => {
            append({
              operator: 'or',
              operand: {
                expressions: [
                  {
                    operand: {
                      field: 'adName',
                      operation: 'contains',
                      value: '',
                    },
                    operator: 'or',
                  },
                ],
              },
            });
          }}
        >
          <Plus className="size-4" />
          Add Grouped Condition
        </Button>
        <div className="gap flex flex-row gap-2.5">
          <Button
            type="button"
            variant="secondary"
            size="small"
            onPress={onClose}
          >
            {LL.cancel()}
          </Button>
          <Button type="submit" size="small">
            {LL.save()}
          </Button>
        </div>
      </div>
    </form>
  );
};

const InsightsExpressionFormExpressionNode: React.FC<{
  deleteGroup: () => void;
  name: `expression.${number}`;
  platform: 'facebook' | 'tiktok';
  customEvents: string[];
  customConversions: Array<{ facebookId: string; name: string }>;
  field: InsightsFilterExpressionNode;
}> = ({
  deleteGroup,
  name,
  customEvents,
  customConversions,
  field,
  platform,
}) => {
  // We should only be an InsightsFacebookFilterExpressionNode here
  if (!(typeof field === 'object' && 'operand' in field)) {
    return null;
  }

  if (!('expressions' in field.operand)) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 rounded-md bg-purple-50 p-3">
        <RecursiveInsightsFilterFormExpressionElementExpressions
          platform={platform}
          deleteGroup={deleteGroup}
          name={`${name}.operand`}
          customEvents={customEvents}
          customConversions={customConversions}
        />
      </div>
    </div>
  );
};

const InsightsFilterFormExpressionFilter: React.FC<{
  name:
    | `expression.${number}.operand`
    | `expression.${number}.operand.left`
    | `expression.${number}.operand.right`;
  operator: 'and' | 'or' | null;
  platform: 'facebook' | 'tiktok';
  customEvents: string[];
  customConversions: Array<{ facebookId: string; name: string }>;
  field: InsightsFilter;
  toggleOperator: ((operator: 'and' | 'or') => void) | null;
  updateFilter: (filter: InsightsFilterV2) => void;
  removeFilter: () => void;
}> = ({
  name,
  operator,
  platform,
  customEvents,
  customConversions,
  field,
  toggleOperator,
  updateFilter,
  removeFilter,
}) => {
  // Filter
  return (
    <div className="flex flex-row gap-2">
      {operator === 'or' ? (
        <Button
          variant="secondary"
          size="small"
          onPress={() => toggleOperator?.('and')}
        >
          <RefreshCw05 className="size-4" />
          or
        </Button>
      ) : operator === 'and' ? (
        <Button
          variant="secondary"
          size="small"
          onPress={() => toggleOperator?.('or')}
        >
          <RefreshCw05 className="size-4" />
          and
        </Button>
      ) : null}

      <div className="grid auto-cols-fr grid-flow-col grid-rows-1 gap-2">
        <InsightsFormFilterMetricFieldSelect
          platform={platform}
          prefix={name}
          customConversions={customConversions}
          customEvents={customEvents}
          onChange={(nextMetric) => {
            const nextFilter = transformInsightsFilterMetric(
              platform,
              nextMetric,
              field.operation
            );
            if (nextFilter) {
              updateFilter(nextFilter);
            }
          }}
        />

        <InsightsFormFilterMetricOperationSelect
          platform={platform}
          prefix={name}
          onChange={(e: string) => {
            const nextFilter = transformInsightsFilterOperation(
              platform,
              e as InsightsMetricOperationV2,
              field
            );
            if (nextFilter) {
              updateFilter(nextFilter);
            }
          }}
        />

        <InsightsFilterFormFieldValueInputs platform={platform} prefix={name} />
      </div>

      <Button
        className="self-center p-2 hover:bg-purple-100"
        variant="text"
        size="small"
        onPress={removeFilter}
      >
        <Trash01 className="size-5" />
      </Button>
    </div>
  );
};

const RecursiveInsightsFilterFormExpressionElementExpressions: React.FC<{
  name: string;
  platform: 'facebook' | 'tiktok';
  customEvents: string[];
  customConversions: Array<{ facebookId: string; name: string }>;
  deleteGroup: () => void;
}> = ({ name, customEvents, customConversions, deleteGroup, platform }) => {
  const { control } = useFormContext<InsightsExpressionForm>();
  const { fields, append, update, remove } = useFieldArray({
    name: `${name}.expressions` as 'expression',
    control,
  });

  return (
    <>
      {fields.map((x, i) =>
        'left' in x.operand ? null : 'expressions' in x.operand ? (
          <RecursiveInsightsFilterFormExpressionElementExpressions
            platform={platform}
            key={x.id}
            name={`${name}.expressions.${i}.operand`}
            customEvents={customEvents}
            customConversions={customConversions}
            deleteGroup={deleteGroup}
          />
        ) : (
          <InsightsFilterFormExpressionFilter
            platform={platform}
            toggleOperator={(operator) => {
              update(i - 1, {
                ...fields[i - 1],
                operator,
              });
            }}
            updateFilter={(filter) =>
              update(i, { ...fields[i], operand: filter })
            }
            removeFilter={() => remove(i)}
            key={x.id}
            field={x.operand}
            name={
              `${name}.expressions.${i}.operand` as `expression.${number}.operand`
            }
            operator={i > 0 ? fields[i - 1].operator : null}
            customEvents={customEvents}
            customConversions={customConversions}
          />
        )
      )}
      <div className="flex flex-row justify-between gap-2">
        <Button
          className="hover:bg-purple-100"
          variant="text"
          size="small"
          onPress={() => {
            append({
              operator: 'or',
              operand: { field: 'adName', operation: 'contains', value: '' },
            });
          }}
        >
          Add Condition
        </Button>
        <Button
          className="hover:bg-purple-100"
          variant="text"
          size="small"
          onPress={deleteGroup}
        >
          Remove Group
        </Button>
      </div>
    </>
  );
};
